import {
  ChangeDetectorRef,
  ElementRef,
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  PLATFORM_ID
} from "@angular/core";
import { Router } from "@angular/router";
import { MainState } from "@app/store/state";
import { ForumPostSearchInterface } from "@core/interfaces/forum-post-search.interface";
import { UserProfileInterface } from "@core/interfaces/user-profile.interface";
import { ForumPostSearchApiService } from "@core/services/api/forum/forum-post-search-api.service";
import { PaginatedApiResultInterface } from "@core/services/api/interfaces/paginated-api-result.interface";
import { ClassicRoutesService } from "@core/services/classic-routes.service";
import { DeviceService } from "@core/services/device.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { selectCurrentUserProfile } from "@features/account/store/auth.selectors";
import { SearchModelInterface } from "@features/search/interfaces/search-model.interface";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { ScrollableSearchResultsBaseComponent } from "@shared/components/search/scrollable-search-results-base/scrollable-search-results-base.component";
import { Observable } from "rxjs";
import { take } from "rxjs/operators";

@Component({
  selector: "astrobin-forum-post-search",
  templateUrl: "./forum-post-search.component.html",
  styleUrls: ["./forum-post-search.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForumPostSearchComponent extends ScrollableSearchResultsBaseComponent<ForumPostSearchInterface> {
  @Input()
  model: SearchModelInterface;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly forumPostSearchApiService: ForumPostSearchApiService,
    public readonly classicRoutesService: ClassicRoutesService,
    public readonly windowRefService: WindowRefService,
    public readonly elementRef: ElementRef,
    public readonly translateService: TranslateService,
    @Inject(PLATFORM_ID) public readonly platformId: Record<string, unknown>,
    public readonly utilsService: UtilsService,
    public readonly changeDetectorRef: ChangeDetectorRef,
    private readonly deviceService: DeviceService,
    private readonly router: Router
  ) {
    super(store$, windowRefService, elementRef, platformId, translateService, utilsService, changeDetectorRef);
  }

  fetchData(): Observable<PaginatedApiResultInterface<ForumPostSearchInterface>> {
    return this.forumPostSearchApiService.search({ ...this.model, pageSize: this.pageSize });
  }

  openPost(post: ForumPostSearchInterface) {
    this.store$
      .select(selectCurrentUserProfile)
      .pipe(take(1))
      .subscribe((userProfile: UserProfileInterface) => {
        const useNewForum =
          !userProfile || userProfile.enableNewForumExperience !== false || this.deviceService.isPwa();

        if (useNewForum && post.topicId) {
          // Use new forum URL format
          const postId = post.id.replace("pybb.post.", "");
          void this.router.navigateByUrl(`/forum/topic/${post.topicId}#post-${postId}`);
        } else {
          // Use classic forum URL
          this.windowRefService.nativeWindow.open(
            this.classicRoutesService.FORUM_POST(post.id.replace("pybb.post.", "")),
            "_self"
          );
        }
      });
  }
}
